import { Seo, buildArticleSchema } from '../../core/seo';

const privacyArticleSchema = buildArticleSchema({
  headline: 'Privacy Policy',
  description:
    "HirePanther's Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our platform.",
  url: 'https://uat.hirepanther.ca/privacy-policy',
  datePublished: '2025-12-01',
  dateModified: '2025-12-01',
  publisher: {
    '@type': 'Organization',
    name: 'HirePanther',
    url: 'https://uat.hirepanther.ca',
  },
});

const PrivacyPolicy = () => {
  return (
    <div className=" text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="Privacy Policy"
        description="HirePanther's Privacy Policy explains how we collect, use, disclose, and protect your personal information when you use our platform."
        canonicalPath="/privacy-policy"
        structuredData={privacyArticleSchema}
      />
      <div className="min-h-screen bg-white text-[#284F60]  px-6 py-12 md:px-16 lg:px-32 ">
        {/* Page Header */}
        <header className="text-left mb-12">
          <h1 className="text-4xl font-bold text-[#284F60] mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-500 mt-3">
            Effective Date: December 01, 2025 | Last Updated: December 01, 2025
          </p>
        </header>

        {/* Introduction */}
        <section className="mb-10">
          <p className="text-lg leading-relaxed">
            HirePanther (“we,” “our,” “us”) is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, disclose,
            and protect your personal information when you use our website,
            mobile apps, and related services (the “Platform”).
          </p>
          <p className="mt-4 text-lg leading-relaxed">
            By using HirePanther, you agree to this Policy. If you do not agree,
            please do not use the Platform.
          </p>
        </section>

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            1. Information We Collect
          </h2>
          <p className="mb-4">We collect the following types of information:</p>

          <h3 className="text-xl font-medium text-[#2D617A] mt-4">
            a) Information you provide directly
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Account details (name, email, phone, password).</li>
            <li>Profile information (photo, address, service preferences).</li>
            <li>
              Booking details (service type, time, location, special
              instructions).
            </li>
            <li>
              Payment information (processed securely via third-party providers;
              we do not store card numbers).
            </li>
            <li>Reviews, messages, and other content you post.</li>
          </ul>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            b) Information collected automatically
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>
              Device and browser data (IP address, operating system, type of
              device).
            </li>
            <li>
              Usage data (pages visited, features used, clicks, search queries).
            </li>
            <li>Cookies and similar technologies (see Cookies Policy).</li>
          </ul>

          <h3 className="text-xl font-medium text-[#2D617A] mt-6">
            c) Information from third parties
          </h3>
          <ul className="list-disc list-inside space-y-2 mt-2 ml-4">
            <li>Identity verification services (for fraud prevention).</li>
            <li>Payment processors (transaction status).</li>
            <li>Social login providers (if you choose to link accounts).</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            2. How We Use Your Information
          </h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>Provide and manage the Platform.</li>
            <li>Process bookings and payments.</li>
            <li>Verify identity and reduce fraud.</li>
            <li>
              Communicate with you about your account, bookings, and support
              requests.
            </li>
            <li>
              Send promotions, offers, or service updates (you may opt out).
            </li>
            <li>Improve and personalize the Platform.</li>
            <li>Comply with legal and regulatory requirements.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            3. How We Share Your Information
          </h2>
          <p className="mb-4">We may share information with:</p>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              Providers and Customers: To fulfill a booking, relevant
              information is shared between parties.
            </li>
            <li>Payment processors: To handle secure transactions.</li>
            <li>
              Service providers: For hosting, analytics, customer support, and
              communications.
            </li>
            <li>
              Legal authorities: If required by law, legal process, or to
              protect rights and safety.
            </li>
            <li>
              Business transfers: If we merge, sell, or restructure, your data
              may transfer with the business.
            </li>
          </ul>
          <p className="mt-4">We do not sell your personal information.</p>
        </section>

        {/* Remaining Sections */}
        {[
          {
            title: '4. Data Retention',
            content: [
              'Provide the Platform.',
              'Comply with legal, tax, and accounting obligations.',
              'Resolve disputes and enforce agreements.',
            ],
          },
          {
            title: '5. Your Rights',
            content: [
              'Access: Request a copy of the information we hold about you.',
              'Correction: Request updates or corrections.',
              'Deletion: Request deletion of certain personal information.',
              'Portability: Request transfer of your information to another service.',
              'Withdraw consent: Opt out of marketing or revoke permissions.',
            ],
          },
          {
            title: '6. Security',
            content: [
              'Encryption of sensitive information.',
              'Secure servers and firewalls.',
              'Access controls and monitoring.',
              'No method of transmission is 100% secure, but we work to keep your data safe.',
            ],
          },
          {
            title: '7. Children’s Privacy',
            content: [
              'HirePanther is not directed to children under 13 years of age. We do not knowingly collect personal information from children. If we learn that we have, we will delete it promptly.',
            ],
          },
          {
            title: '8. International Users',
            content: [
              'Our Platform is primarily designed for use in Canada. If you access it from elsewhere, your information may be transferred to and stored in Canada.',
            ],
          },
          {
            title: '9. Third-Party Links & Services',
            content: [
              'The Platform may link to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of those third parties.',
            ],
          },
          {
            title: '10. Cookies & Tracking',
            content: [
              'Authentication and session management.',
              'Analytics (to understand usage).',
              'Advertising (to deliver relevant promotions).',
              'You can manage cookies in your browser settings. See our Cookie Policy for details.',
            ],
          },
          {
            title: '11. Updates to this Policy',
            content: [
              'We may update this Privacy Policy from time to time. Updates will be posted at /legal/privacy with a new effective date. Continued use of HirePanther means you accept the revised Policy.',
            ],
          },
        ].map((section, idx) => (
          <section key={idx} className="mb-10">
            <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
              {section.title}
            </h2>
            <ul className="list-inside space-y-2 ml-4">
              {section.content.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>
        ))}

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            12. Contact Us
          </h2>
          <p className="text-lg leading-relaxed">
            For privacy questions, requests, or complaints:
          </p>
          <p className="mt-2">
           <a
              href="mailto:privacy@hirepanther.ca"
              className="text-blue-500 hover:underline"
            >
              privacy@hirepanther.ca
            </a>
          </p>
          <p className="mt-2 text-sm text-[#284F60]">
            If you are in Quebec or another Canadian jurisdiction with
            additional privacy laws, you may also contact the applicable privacy
            regulator.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
